import { useMutation, useQueryClient } from "@tanstack/react-query";
import { terminateWorkflowCase } from "../api";
import { wmErrorMessage } from "../api/wm-client";
import { toast } from "../utils/toast";

export interface TerminateFlowInput {
  caseUid: string;
  reason?: string;
  attachmentUids?: string[];
}

/**
 * §Terminate: the client decided not to continue — flag the application TERMINATED in WM.
 *
 * On success WM has already halted the flow and hidden the case from every CRM read, so invalidating
 * the whole nawy-now tree is the entire UI transition: case resolution refetches, finds no case, and
 * the tab renders the start-flow state again (a fresh application can be started for the lead).
 * The lead/kanban surfaces fed by the asynchronous CRM replica catch up on their own next refetch.
 */
export const useTerminateFlow = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ caseUid, reason, attachmentUids }: TerminateFlowInput) =>
      terminateWorkflowCase(caseUid, reason, attachmentUids),
    onSuccess: () => {
      toast.success("Flow terminated");
      queryClient.invalidateQueries({ queryKey: ["nawy-now"] });
    },
    // The server's copy verbatim — a 409 names WHY ("already terminated" / "only an active
    // application…") and a 403 names the missing permission; a generic message would strand the agent.
    onError: (error: unknown) => toast.error(wmErrorMessage(error)),
  });
};
